/**
 * The browser side of `GET /transit` (cities plan C4.2) — the same auth shape
 * as `tickets.ts` and `groceryAisles.ts`: the Supabase bearer token, the
 * Worker's URL from the environment. **The phone never calls the MBTA**: the
 * Worker caches for a minute and holds any key there ever is.
 *
 * The rules (`trainPlan`, `cardView`, …) are in `trainPlan.ts`, which has no
 * imports a test cannot load; this file is the part that needs React Query
 * and the session. `trainPlan` is re-exported so the brief's name works.
 *
 * ## When it fails
 *
 * A failed fetch must show the scheduled times, not nothing. Two things make
 * that so: every query is persisted for a week (`store.ts`), so the last good
 * answer is still here offline; and `isLive` (trainPlan.ts) calls an answer
 * live only if it was read from predictions in the last five minutes, so an
 * old one is drawn as a schedule — no live dots, a "schedule" tag. A card
 * with no answer at all, ever, is not drawn.
 */
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './auth'
import { supabase } from './supabase'
import { TRAIN_STOPS, type TransitResponse } from './trainPlan'
import type { RegionPlace } from './currentRegion'

export { trainPlan, cardView, planLines, isLive, TRAIN_STOPS, type Train, type TransitResponse } from './trainPlan'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

/** A live answer is re-read this often while Today is on screen. The Worker
 *  holds one for 60 s and counts only the calls it has to make to the MBTA
 *  against a daily cap of 60, so this is a pace and not a way to spend it. */
const REFETCH_MS = 3 * 60_000
const LIVE_STALE_MS = 90_000
const SCHEDULE_STALE_MS = 15 * 60_000

export async function fetchTransit(stop: string, route: string, date: string | null): Promise<TransitResponse> {
  if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Not signed in')
  const q = `stop=${encodeURIComponent(stop)}&route=${encodeURIComponent(route)}${date ? `&date=${date}` : ''}`
  const res = await fetch(`${WORKER_URL}/transit?${q}`, { headers: { authorization: `Bearer ${token}` } })
  if (!res.ok) throw new Error(`Trains failed: ${res.status}`)
  return res.json()
}

/**
 * The trains at the current region's place's stop. Nothing — disabled, no
 * request — where the place has no `transit_stop` (Berlin, or Concord before
 * the migration) or names a stop the Worker would refuse.
 *
 * `date` (YYYY-MM-DD, a service day) asks for that day's schedule and nothing
 * live, for the sheet's plan on an event that is not tonight; null is "now",
 * which Today's card and tonight's events share under one key. `active` false
 * (Today is mounted behind another tab) stops the polling.
 */
export function useTrains(
  place: Pick<RegionPlace, 'transit_stop'> | null,
  opts: { date?: string | null; active?: boolean } = {},
) {
  const { session } = useAuth()
  const stop = place?.transit_stop ?? null
  const info = stop ? TRAIN_STOPS.get(stop) : undefined
  const date = opts.date ?? null
  return useQuery<TransitResponse>({
    queryKey: ['transit', stop, date ?? 'now'],
    queryFn: () => fetchTransit(stop!, info!.route, date),
    enabled: !!session && !!info,
    staleTime: date ? SCHEDULE_STALE_MS : LIVE_STALE_MS,
    refetchInterval: date || opts.active === false ? false : REFETCH_MS,
  })
}
